import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { Car, Clock, Mail, MapPin, Phone, User, Wrench } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { User as UserType } from '../../../../shared/types/user.types';
import { Garage } from '../../../../shared/types/garage.types';
import { Vehicle } from '../../../../shared/types/vehicle.types';
import { VehicleService } from '../../../../shared/types/vehicle-service.types';
import { SettingListItem } from '../../../../shared/types/setting.types';
import useSettingLists from '../../wrappers/use-setting-list-item';
import useVehicle from '../../wrappers/use-vehicle';
import useVehicleService from '../../wrappers/use-vehicle-service';
import useGarage from '../../wrappers/use-garage';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import { garageDetailStyles } from './GarageDetail.styles';

const InfoRow: React.FC<{ label: string; value: React.ReactNode; last?: boolean }> = ({ label, value, last }) => (
    <>
        <Box sx={garageDetailStyles.infoRow}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
            {typeof value === 'string'
                ? <Typography variant="body2">{value}</Typography>
                : value}
        </Box>
        {!last && <Divider />}
    </>
);

const DealerGarageDetail: React.FC = () => {
    const { uid } = useParams<{ uid: string }>();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const settingLists = useSettingLists();

    const { list: listVehicles } = useVehicle();
    const { list: listServices } = useVehicleService();
    const { get: getGarage } = useGarage();
    const [activeTab, setActiveTab] = useState(0);
    const [user, setUser] = useState<UserType | null>(null);
    const [garage, setGarage] = useState<Garage | null>(null);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [services, setServices] = useState<VehicleService[]>([]);
    const [serviceTypeMap, setServiceTypeMap] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        settingLists.list('serviceTypes').then((items) => {
            setServiceTypeMap(Object.fromEntries(items.map((i: SettingListItem) => [i.id, i.label])));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!uid) return;

        const fetchData = async () => {
            // Load user
            const userSnap = await getDoc(doc(firestore, 'user', uid));
            if (!userSnap.exists()) {
                setNotFound(true);
                setLoading(false);
                return;
            }
            const userData = { uid: userSnap.id, ...userSnap.data() } as UserType;
            setUser(userData);

            // Load garage
            if (userData.garageUid) {
                const garage = await getGarage(userData.garageUid);
                if (garage) {
                    setGarage(garage);

                    // Load vehicles in this garage
                    const vehicleList = await listVehicles({ garageUid: garage.uid });
                    setVehicles(vehicleList);

                    // Load all services for this garage's vehicles
                    const serviceList = vehicleList.length > 0
                        ? (await Promise.all(vehicleList.map(v => listServices({ vehicleUid: v.uid })))).flat()
                        : [];
                    setServices(serviceList);
                }
            }

            setLoading(false);
        };

        fetchData();
    }, [uid, getGarage, listServices, listVehicles]);

    if (loading) {
        return (
            <Box sx={garageDetailStyles.loading}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !user) {
        return <Alert severity="error">{t('garageDetail.notFound')}</Alert>;
    }

    const address = garage?.address;
    const addressStr = address
        ? `${address.street} ${address.houseNumber}, ${address.zip} ${address.city}`
        : '—';

    return (
        <Box sx={garageDetailStyles.root}>
            {/* Info o zákazníkovi a garáži */}
            <Paper elevation={0} sx={garageDetailStyles.borderedPaper}>
                <Box sx={garageDetailStyles.sectionHeader}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{t('garageDetail.customerTitle')}</Typography>
                        <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                    </Box>
                </Box>

                <InfoRow
                    label={t('garageDetail.nameLabel')}
                    value={<Typography variant="body2" sx={garageDetailStyles.contactValue}><User size={14} />{user.name}</Typography>}
                />
                <InfoRow
                    label={t('garageDetail.phoneLabel')}
                    value={
                        user.phone
                            ? <Typography variant="body2" sx={garageDetailStyles.contactValue}><Phone size={14} />{user.phone}</Typography>
                            : <Typography variant="body2" color="text.secondary">—</Typography>
                    }
                />
                <InfoRow
                    label={t('garageDetail.emailLabel')}
                    value={<Typography variant="body2" sx={garageDetailStyles.contactValue}><Mail size={14} />{user.email}</Typography>}
                />
                <InfoRow
                    label={t('garageDetail.garageAddressLabel')}
                    value={
                        address
                            ? <Typography variant="body2" sx={garageDetailStyles.contactValue}><MapPin size={14} />{addressStr}</Typography>
                            : <Typography variant="body2" color="text.secondary">—</Typography>
                    }
                    last
                />
            </Paper>

            {/* Záložky: vozidla / servisy */}
            <Paper elevation={0} sx={garageDetailStyles.borderedPaper}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={garageDetailStyles.tabs}
                >
                    <Tab
                        icon={<Car size={18} />}
                        iconPosition="start"
                        label={t('garageDetail.vehiclesTab', { count: vehicles.length })}
                        sx={garageDetailStyles.tab}
                    />
                    <Tab
                        icon={<Wrench size={18} />}
                        iconPosition="start"
                        label={t('garageDetail.servicesTab', { count: services.length })}
                        sx={garageDetailStyles.tab}
                    />
                </Tabs>

                {/* Vozidla */}
                {activeTab === 0 && (
                    vehicles.length === 0 ? (
                        <Box sx={garageDetailStyles.emptyState}>
                            <Typography variant="body2" color="text.secondary">{t('garageDetail.noVehicles')}</Typography>
                        </Box>
                    ) : (
                        vehicles.map((vehicle, i) => (
                            <Box key={vehicle.uid}>
                                {i > 0 && <Divider />}
                                <Box
                                    component={Link}
                                    to={`/app/service-desk/vehicles/${vehicle.uid}`}
                                    sx={garageDetailStyles.vehicleLinkRow}
                                >
                                    <Box sx={garageDetailStyles.vehicleRowMain}>
                                        <Box sx={garageDetailStyles.vehicleIcon}>
                                            <Car size={18} color="#009fe3" />
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>{vehicle.model}</Typography>
                                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                                VIN: {vehicle.uid}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={garageDetailStyles.vehicleRowAside}>
                                        {vehicle.warranty?.validUntil && (
                                            <Chip
                                                label={new Date(vehicle.warranty.validUntil) > new Date() ? t('garageDetail.warrantyValid') : t('garageDetail.warrantyExpired')}
                                                color={new Date(vehicle.warranty.validUntil) > new Date() ? 'success' : 'default'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                        <Typography variant="caption" color="primary.main" fontWeight={500}>
                                            {t('garageDetail.viewDetail')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    )
                )}

                {/* Servisní záznamy */}
                {activeTab === 1 && (
                    services.length === 0 ? (
                        <Box sx={garageDetailStyles.emptyState}>
                            <Typography variant="body2" color="text.secondary">{t('garageDetail.noServices')}</Typography>
                        </Box>
                    ) : (
                        services.map((service, i) => (
                            <Box key={service.id}>
                                {i > 0 && <Divider />}
                                <Box sx={garageDetailStyles.serviceRow}>
                                    <Box sx={garageDetailStyles.serviceRowMain}>
                                        <Wrench size={20} color="#757575" />
                                        <Box>
                                            <Typography variant="body2" fontWeight={500} color="primary.main">
                                                {serviceTypeMap[service.serviceType] ?? service.serviceType}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                VIN: {service.vehicleUid}
                                            </Typography>
                                            {service.description && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {service.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={garageDetailStyles.serviceRowAside}>
                                        <Typography variant="body2" color="text.secondary" sx={garageDetailStyles.serviceDate}>
                                            <Clock size={16} />
                                            {formatDate(service.createdAt)}
                                        </Typography>
                                        <Box
                                            component={Link}
                                            to={`/app/service-desk/vehicles/${service.vehicleUid}`}
                                            sx={garageDetailStyles.serviceMachineLink}
                                        >
                                            {t('garageDetail.machineLink')}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    )
                )}
            </Paper>
        </Box>
    );
};

export default DealerGarageDetail;
